import React, { useEffect, useSyncExternalStore } from "react";
import { useBlocker } from "react-router-dom";

import * as ozon from "@/api/ozon_fbs";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { ScanBanner } from "@/pages/fbs/ScanBanner";
import { shipRefusal } from "@/pages/ozon/MarkErrors";

/**
 * Автосборка + печать на скане, закрывшем отправление (решение 30.09.2026):
 * скан вернул `posting_complete` → `ship` → при успехе `label`. Без диалога —
 * подтверждение осталось только у ручной «Собрать» в «Коробках».
 *
 * ⚠️ Очередь СТРОГО последовательная (FIFO): ship+label следующего отправления
 * стартует только после того, как предыдущее дошло до конца. Иначе этикетки
 * выходили бы из принтера не в порядке сканов, и кладовщик клеил бы чужую
 * наклейку на коробку в руках. Скан при этом НЕ ждёт: очередь живёт отдельно.
 *
 * ⚠️ Отказ сборки — этикетку НЕ зовём (её до сборки у Ozon не существует), а
 * причину показываем плашкой, которая сама не исчезает. Отправление при этом
 * уже в «Разборе» — он выводится из следов, повтор — оттуда.
 */
export type ChainPhase =
  | "queued"
  | "shipping"
  | "printing"
  | "done"
  | "ship_failed"
  | "ship_unknown"
  | "label_failed";

export interface ChainEntry {
  key: number;
  postingId: string;
  postingNumber: string;
  phase: ChainPhase;
  /** Текст ошибки бэка — у `*_failed`/`ship_unknown`. */
  error?: string;
  /** Отказ сборки по кодам маркировки — та же разбивка, что у ручной «Собрать». */
  marks?: { title: string; lines: string[] } | null;
  /** `/ship` ответил ошибкой, но статус отправления — собрано (сбой ПОСЛЕ
   *  сборки: поставка, опрос, параллельная ручная «Собрать»). Цепочка идёт к
   *  этикетке, а текст ошибки остаётся на плашке. */
  shipWarning?: string;
  /** `ship_unknown`: не удалось перечитать отправление после ошибки ship. */
  readError?: string;
}

const ACTIVE: ReadonlySet<ChainPhase> = new Set(["queued", "shipping", "printing"]);

interface Hooks {
  /** Шаг вернул обновлённое отправление — положить в кеш, как ручные мутации. */
  onPosting: (p: ozon.OzonPosting) => void;
  /** Шаг упал — «Разбор» и пиклист сдвинулись, их надо перечитать. */
  onFailure: () => void;
}

/**
 * ⚠️ Очередь ОДНА НА ПРИЛОЖЕНИЕ (модульный стор), а не на экземпляр хука.
 * Цепочка намеренно переживает уход с экрана (`LeaveGuard`), а очередь в
 * `useRef` умирала бы вместе с экраном: вернувшись, кладовщик получал бы
 * ВТОРУЮ очередь и второй цикл, и ship/label двух отправлений шли бы
 * параллельно — этикетки из принтера не в порядке сканов. Экран лишь
 * подписывается на состояние стора.
 *
 * Колбэки (`Hooks`) — последнего смонтированного экрана; после ухода они
 * остаются: пишут в кеш react-query, который живёт дольше экрана.
 */
const store = {
  entries: [] as ChainEntry[],
  queue: [] as ChainEntry[],
  running: false,
  seq: 0,
  hooks: null as Hooks | null,
  listeners: new Set<() => void>(),
};

function emit(): void {
  store.listeners.forEach((l) => l());
}

function setEntries(fn: (es: ChainEntry[]) => ChainEntry[]): void {
  store.entries = fn(store.entries);
  emit();
}

function patch(key: number, p: Partial<ChainEntry>): void {
  setEntries((es) => {
    const next = es.map((e) => (e.key === key ? { ...e, ...p } : e));
    // Успех показываем только последний: плашек «напечатано» копилось бы
    // по одной на коробку. Отказы, незаконченное и успех с оговоркой
    // (`shipWarning`) — держим.
    if (p.phase === "done") {
      return next.filter((e) => e.phase !== "done" || e.key === key || !!e.shipWarning);
    }
    return next;
  });
}

function hooks(): Hooks {
  // ⚠️ Без колбэков цепочка не запускается вовсе (`enqueue` зовёт только
  // смонтированный хук), так что их отсутствие здесь — баг, а не режим.
  if (!store.hooks) throw new Error("autoship: колбэки экрана не заданы");
  return store.hooks;
}

async function pump(): Promise<void> {
  if (store.running) return;
  store.running = true;
  try {
    while (store.queue.length) {
      const job = store.queue.shift()!;
      patch(job.key, { phase: "shipping" });
      let shipped: ozon.OzonPosting;
      let shipWarning: string | undefined;
      try {
        shipped = await ozon.shipPosting(job.postingId);
      } catch (err) {
        // ⚠️ Ошибка `/ship` ≠ «не собрано». Ручка падает и ПОСЛЕ принятой
        // сборки: поставка занята (409), параллельная ручная «Собрать»
        // (409 «уже собирается»), опрос статуса вышел за 60 с (502). Решает
        // СТАТУС отправления (`isAssembled`, правило бэка), а не код ошибки.
        const error = extractErrorMessage(err, "Не удалось собрать в Ozon");
        let fresh: ozon.OzonPosting;
        try {
          fresh = await ozon.getPosting(job.postingId);
        } catch (readErr) {
          patch(job.key, {
            phase: "ship_unknown",
            error,
            readError: extractErrorMessage(readErr, "Не удалось перечитать отправление"),
          });
          hooks().onFailure();
          continue;
        }
        if (!ozon.isAssembled(fresh)) {
          patch(job.key, { phase: "ship_failed", error, marks: shipRefusal(err) });
          hooks().onFailure();
          continue;
        }
        shipped = fresh;
        shipWarning = error;
        // Хвост (поставка) виден в «Разборе» — перечитать его.
        hooks().onFailure();
      }
      hooks().onPosting(shipped);
      patch(job.key, { phase: "printing", shipWarning });
      try {
        const printed = await ozon.printLabel(job.postingId);
        hooks().onPosting(printed);
        patch(job.key, { phase: "done" });
      } catch (err) {
        patch(job.key, {
          phase: "label_failed",
          error: extractErrorMessage(err, "Не удалось напечатать этикетку"),
        });
        hooks().onFailure();
      }
    }
  } finally {
    store.running = false;
  }
}

function enqueue(postingId: string, postingNumber: string): void {
  const job: ChainEntry = {
    key: ++store.seq,
    postingId,
    postingNumber,
    phase: "queued",
  };
  store.queue.push(job);
  setEntries((es) => [...es, job]);
  void pump();
}

function dismiss(key: number): void {
  setEntries((es) => es.filter((e) => e.key !== key));
}

function subscribe(l: () => void): () => void {
  store.listeners.add(l);
  return () => store.listeners.delete(l);
}

const snapshot = (): ChainEntry[] => store.entries;

/** Только для тестов: пустой стор между тестами (модуль живёт весь прогон). */
export function resetAutoShipForTests(): void {
  if (store.running || store.queue.length) {
    throw new Error("autoship: сброс при идущей цепочке — тест не дождался её конца");
  }
  store.entries = [];
  store.seq = 0;
  store.hooks = null;
  emit();
}

export function useAutoShip(h: Hooks): {
  entries: ChainEntry[];
  busy: boolean;
  enqueue: (postingId: string, postingNumber: string) => void;
  dismiss: (key: number) => void;
} {
  // Колбэки — свежие на каждый рендер: цикл живёт дольше одного рендера.
  store.hooks = h;
  const entries = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { entries, busy: entries.some((e) => ACTIVE.has(e.phase)), enqueue, dismiss };
}

/**
 * Уход со страницы, пока цепочка не дошла до конца, — спросить, а не
 * прерывать молча. Внутри портала (стрелка, «Поставка», аппаратная «назад»)
 * цепочка ДОДЕЛАЕТСЯ — она не привязана к экрану, — но её исход кладовщик уже
 * не увидит; закрытие вкладки обрывает её на полпути.
 *
 * ⚠️ Блокируем только смену ПУТИ: шторки и диалоги кладут в историю записи с
 * тем же URL (`useBackGuard`), и «назад» над шторкой должен закрывать её, а
 * не спрашивать про уход.
 */
export function LeaveGuard({ busy, current }: { busy: boolean; current: string }): React.ReactElement | null {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      busy && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!busy) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  // Цепочка кончилась, пока висел вопрос, — уходить уже можно без него.
  useEffect(() => {
    if (blocker.state === "blocked" && !busy) blocker.proceed();
  }, [blocker, busy]);

  if (blocker.state !== "blocked") return null;
  // ⚠️ Свой оверлей, а не `Dialog`: тот кладёт запись в историю
  // (`useBackGuard`), а заблокированный переход роутер откатывает по своей
  // арифметике истории — лишняя запись её сбила бы.
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4">
      <div role="dialog" className="w-full max-w-sm rounded-2xl bg-white p-3.5">
        <div className="text-[16px] font-bold">Идёт сборка в Ozon</div>
        <div className="mt-1 text-[12px] text-slate-600">
          Сейчас: {current}. Если уйти, сборка и печать продолжатся, но их
          результат на экране вы не увидите — проверяйте «Разбор».
        </div>
        <div className="mt-3 flex gap-2">
          <Button variant="outline" className="h-12 flex-1" onClick={() => blocker.reset()}>
            Остаться
          </Button>
          <Button variant="destructive" className="h-12 flex-1" onClick={() => blocker.proceed()}>
            Уйти
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Плашки цепочек: идущая, последний успех, отказы (висят до закрытия). */
export function AutoShipBanners({
  entries,
  onDismiss,
  onOpenBoxes,
}: {
  entries: ChainEntry[];
  onDismiss: (key: number) => void;
  /** Отказ по кодам — открыть «Коробки», где у кода есть «Заменить код». */
  onOpenBoxes: (marks: { title: string; lines: string[] }) => void;
}): React.ReactElement | null {
  const waiting = entries.filter((e) => e.phase === "queued");
  const shown = entries.filter((e) => e.phase !== "queued");
  if (!shown.length && !waiting.length) return null;
  return (
    <div className="space-y-2">
      {shown.map((e) => {
        switch (e.phase) {
          case "shipping":
            return (
              <ScanBanner key={e.key} tone="info" title={`Собираем в Ozon: № ${e.postingNumber}`} onClose={null}>
                {waiting.length > 0 && `в очереди ещё ${waiting.length}`}
              </ScanBanner>
            );
          case "printing":
            return (
              <ScanBanner key={e.key} tone="info" title={`Печатаем этикетку: № ${e.postingNumber}`} onClose={null}>
                {e.shipWarning && <ShipWarning text={e.shipWarning} />}
                {waiting.length > 0 && `в очереди ещё ${waiting.length}`}
              </ScanBanner>
            );
          case "done":
            return e.shipWarning ? (
              <ScanBanner
                key={e.key}
                tone="warn"
                title={`Собрано в Ozon, этикетка отправлена на принтер: № ${e.postingNumber}`}
                onClose={() => onDismiss(e.key)}
              >
                <ShipWarning text={e.shipWarning} />
              </ScanBanner>
            ) : (
              <ScanBanner
                key={e.key}
                tone="ok"
                title={`Этикетка отправлена на принтер: № ${e.postingNumber}`}
                onClose={() => onDismiss(e.key)}
              />
            );
          case "ship_unknown":
            return (
              <ScanBanner
                key={e.key}
                tone="error"
                title={`Сборка не подтверждена: № ${e.postingNumber}`}
                onClose={() => onDismiss(e.key)}
              >
                <div className="break-words">{e.error}</div>
                <div className="mt-1 break-words">
                  Проверить статус не удалось: {e.readError}
                </div>
                <div className="mt-1">
                  Этикетка не печаталась. Собрано ли — смотрите в «Разборе» и «Коробках».
                </div>
              </ScanBanner>
            );
          case "ship_failed":
            return (
              <ScanBanner
                key={e.key}
                tone="error"
                title={
                  e.marks
                    ? `${e.marks.title}: № ${e.postingNumber}`
                    : `Не собрано в Ozon: № ${e.postingNumber}`
                }
                onClose={() => onDismiss(e.key)}
                actions={
                  e.marks ? (
                    <Button size="sm" variant="outline" onClick={() => onOpenBoxes(e.marks!)}>
                      Открыть «Коробки»
                    </Button>
                  ) : undefined
                }
              >
                {e.marks ? (
                  <ul className="space-y-0.5">
                    {e.marks.lines.map((l) => (
                      <li key={l} className="break-words">{l}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="break-words">{e.error}</div>
                )}
                <div className="mt-1">Этикетка не печаталась. Отправление — в «Разборе».</div>
              </ScanBanner>
            );
          case "label_failed":
            return (
              <ScanBanner
                key={e.key}
                tone="error"
                title={`Собрано, этикетка не напечатана: № ${e.postingNumber}`}
                onClose={() => onDismiss(e.key)}
              >
                {e.shipWarning && <ShipWarning text={e.shipWarning} />}
                <div className="break-words">{e.error}</div>
                <div className="mt-1">Отправление — в «Разборе», печать повторите оттуда.</div>
              </ScanBanner>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

/**
 * `/ship` ответил ошибкой, а статус — собрано. Чаще всего это привязка к
 * поставке («Поставка сейчас занята»). ⚠️ «Разбор» её не чинит: у собранного
 * нет кнопки «Собрать», а «К поставке» без поставки неактивна. Подбирает такое
 * отправление синхронизация — «Обновить заказы» (`adopt_orphans`: собранное
 * со сканами и без поставки).
 */
function ShipWarning({ text }: { text: string }): React.ReactElement {
  return (
    <div className="break-words">
      Ozon сборку подтвердил, но ответ был с ошибкой: {text} Если отправление не
      попало в поставку, его подберёт «Обновить заказы» на главной Ozon.
    </div>
  );
}
